import {  useEffect, useRef, useState } from 'react'
import { returnToFromState } from '@/auth/stepUp'
import { userManager } from '@/auth/config'
import { persistLastAuthError, clearLastAuthError } from '@/lib/bootstrapError'
import { AppErrorScreen } from '@/app/AppErrorScreen'
import '@/shared/styles/global.css'
import { Spinner } from '@/app/Spinners'
import { bumpAuthAttempts, clearAuthAttempts } from '@/app/authAttempts'

export function Callback() {
  const [err, setErr] = useState<{ error: unknown } | null>(null)
  const ran = useRef(false)
  useEffect(() => {
    // The code exchange consumes the one-time auth code; StrictMode's dev
    // double-mount would run it twice, and the second call's guaranteed failure
    // would bump the breaker and flash a spurious error screen.
    if (ran.current) return
    ran.current = true
    userManager.signinRedirectCallback().then((user) => {
      clearAuthAttempts()
      clearLastAuthError()
      // A step-up re-authentication carries where it came from, so an operator who was told to
      // re-authenticate mid-operation lands back on that operation rather than on the dashboard.
      // Validated as a same-origin path: `returnTo` is just state, and `//evil.example` reads as a
      // path to a naive check.
      window.location.replace(returnToFromState(user.state) ?? '/')
    }).catch((e: unknown) => {
      // Do NOT silently swallow: the code-exchange failing here leaves the app
      // unauthenticated with no signal. Log the real oidc-client-ts rejection and
      // persist it, so an auth/config break is diagnosable instead of a blank app.
      // (Raw oidc errors can carry state ids, so the message stays behind the
      // title-click diagnostic rather than on screen.)
      console.error('[auth.callback] signinRedirectCallback failed:', e)
      bumpAuthAttempts()
      persistLastAuthError(e, 'signin-callback')
      setErr({ error: e })
    })
  }, [])
  if (err) {
    return (
      <AppErrorScreen
        title="Sign-in failed"
        message="Sign-in could not complete."
        stage="signin-callback"
        error={err.error}
        action={{ label: 'Try again', onClick: () => { clearAuthAttempts(); window.location.replace('/') } }}
      />
    )
  }
  return <Spinner />
}
